import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { UNSAFE_NavigationContext as NavigationContext, useLocation } from 'react-router-dom'
import { Stack, Typography } from '@mui/material'
import { WarningRounded } from '@mui/icons-material'
import { SmartDialog } from '../SmartDialog'
import createDialogTitle from '../dialogUtil'
import { useSmartTranslation } from '../../hooks/useSmartTranslation'
import {
  clearUnsavedChanges,
  getHasUnsavedChanges,
  subscribeDataSaved,
  subscribeUnsavedChangesConfirmationRequest
} from './unsavedChangesGuard'

const getCurrentPath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`

const getNormalizedPath = (to) => {
  if (typeof to === 'number') return null

  // Navigate can receive a string or an object, so normalize both cases.
  const path = typeof to === 'string'
    ? to
    : `${to?.pathname ?? window.location.pathname}${to?.search ?? ''}${to?.hash ?? ''}`

  try {
    const url = new URL(path, window.location.href)
    return `${url.pathname}${url.search}${url.hash}`
  } catch (exception) {
    return path
  }
}

const getLinkPath = (target) => {
  const link = target.closest('a[href]')
  if (!link) return null

  const url = new URL(link.href, window.location.href)
  return `${url.pathname}${url.search}${url.hash}`
}

const isModifiedClick = (event) => {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0
}

const isInternalLink = (target) => {
  const link = target?.closest?.('a[href]')
  if (!link || link.target || link.hasAttribute('download')) return false

  const href = link.getAttribute('href')
  if (href && href.startsWith('#')) return false

  try {
    return new URL(link.href, window.location.href).origin === window.location.origin
  } catch (exception) {
    return false
  }
}

const isSamePath = (to) => {
  const nextPath = getNormalizedPath(to)
  return nextPath !== null && nextPath === getCurrentPath()
}

const UnsavedChangesGuard = () => {
  const { navigator } = useContext(NavigationContext)
  const location = useLocation()
  const { t_ } = useSmartTranslation()
  const locationKeyRef = useRef(location.key)
  const historyIndexRef = useRef(window.history.state?.idx ?? 0)
  const skipNextPopRef = useRef(false)
  const allowNextNavigationRef = useRef(false)
  const pendingActionRef = useRef(null)
  const [showDialog, setShowDialog] = useState(false)

  const message = t_('You have changes pending saving. If you exit now, you may lose data.')
  const question = t_('Do you want to exit without saving?')

  useEffect(() => {
    return () => {
      pendingActionRef.current = null
      clearUnsavedChanges()
    }
  }, [])

  const requestConfirmation = useCallback((action) => {
    if (!getHasUnsavedChanges()) return true

    // Keep the action to run it only if the user accepts.
    pendingActionRef.current = action
    setShowDialog(true)
    return false
  }, [])

  const handleAccept = () => {
    const action = pendingActionRef.current
    pendingActionRef.current = null
    setShowDialog(false)
    clearUnsavedChanges()

    if (typeof action === 'function') {
      // Avoid showing the dialog again for the accepted navigation.
      allowNextNavigationRef.current = true
      window.setTimeout(() => action(), 0)
    }
  }

  const handleCancel = () => {
    pendingActionRef.current = null
    setShowDialog(false)
  }

  useEffect(() => {
    // Other components can use this event to show the same confirmation dialog.
    return subscribeUnsavedChangesConfirmationRequest((action) => {
      requestConfirmation(action)
    })
  }, [requestConfirmation])

  useEffect(() => {
    const unsubscribeDataSaved = subscribeDataSaved(clearUnsavedChanges)

    return unsubscribeDataSaved
  }, [])

  useEffect(() => {
    const handleClick = (event) => {
      if (isModifiedClick(event) || !isInternalLink(event.target)) return

      const nextPath = getLinkPath(event.target)
      if (!nextPath || nextPath === getCurrentPath()) return

      if (getHasUnsavedChanges()) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        requestConfirmation(() => navigator.push(nextPath))
      }
    }

    // Check the link before React Router changes the url.
    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [navigator, requestConfirmation])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!getHasUnsavedChanges()) return

      // Browser dialog cannot be styled.
      event.preventDefault()
      event.returnValue = message
      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [message])

  useEffect(() => {
    if (!navigator) return undefined

    const originalPush = navigator.push
    const originalReplace = navigator.replace
    const originalGo = navigator.go

    const runConfirmedNavigation = (callback) => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false
        callback()
        return
      }

      // If there are no changes, requestConfirmation returns true and continues.
      if (requestConfirmation(callback)) {
        callback()
      }
    }

    navigator.push = (...args) => {
      if (isSamePath(args[0])) return
      runConfirmedNavigation(() => originalPush(...args))
    }

    navigator.replace = (...args) => {
      if (isSamePath(args[0])) return
      runConfirmedNavigation(() => originalReplace(...args))
    }

    navigator.go = (...args) => {
      runConfirmedNavigation(() => originalGo(...args))
    }

    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
      navigator.go = originalGo
    }
  }, [navigator, requestConfirmation])

  useEffect(() => {
    const handlePopState = (event) => {
      if (skipNextPopRef.current) {
        skipNextPopRef.current = false
        historyIndexRef.current = window.history.state?.idx ?? historyIndexRef.current
        return
      }

      const nextIndex = event.state?.idx ?? historyIndexRef.current
      const delta = nextIndex - historyIndexRef.current
      if (!getHasUnsavedChanges()) {
        historyIndexRef.current = nextIndex
        return
      }

      // Browser already moved. Go back first and continue only if user accepts.
      event.preventDefault()
      event.stopImmediatePropagation()
      skipNextPopRef.current = true
      window.history.go(delta === 0 ? 1 : -delta)
      requestConfirmation(() => {
        window.setTimeout(() => navigator.go(delta), 0)
      })
    }

    window.addEventListener('popstate', handlePopState, true)
    return () => window.removeEventListener('popstate', handlePopState, true)
  }, [navigator, requestConfirmation])

  useEffect(() => {
    if (location.key !== locationKeyRef.current) {
      // If route changed for real, old pending changes no longer apply.
      locationKeyRef.current = location.key
      historyIndexRef.current = window.history.state?.idx ?? historyIndexRef.current
      clearUnsavedChanges()
    }
  }, [location.key])

  return (
    <SmartDialog
      setOpen={showDialog}
      title={createDialogTitle({ color: 'warning', title: t_('Warning'), icon: WarningRounded })}
      renderComponent={
        <Stack spacing={1.5} sx={{ px: 2, py: 1, maxWidth: '34rem' }}>
          <Typography sx={{ textAlign: 'center' }}>
            {message}
          </Typography>
          <Typography sx={{ textAlign: 'center', fontWeight: 600 }}>
            {question}
          </Typography>
        </Stack>
      }
      acceptCallback={handleAccept}
      cancelCallback={handleCancel}
      acceptLabel={t_('Exit without saving')}
      cancelLabel={t_('cancel')}
      close={false}
      minWidth='28rem'
    />
  )
}

export default UnsavedChangesGuard
